import { Box, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { User } from "src/services/dto/Security";
import { z } from "zod";



import FormEngine from "../../revamp/components/FormEngine/FormEngine";
import { FormField } from "../../revamp/components/FormEngine/types";
import AuthService from "../../services/authService";
import UserService from "../../services/userService";
import { API_LOAD_PROVIDERS } from "../../store/actions/appointments";
import NoProviders from "./NoProviders";
import { regex } from "@utils/zod";





interface PersonalInfoProps {
  title?: string;
  subtitle?: string;
  disabledFields?: string[];
  profile?: boolean;
  onContinue: (user: User, providers: User[]) => void;
  initialValues?: any;
}

type states = {
  key: string;
  value: string;
  disabled?: boolean;
};

const PersonalInfo = (props: PersonalInfoProps) => {
  const { user } = AuthService.getAuth();
  if (!user) return null;

  const [states, setStates] = useState<states[]>([]);
  const [showDobError, setShowDobError] = useState(true);
  const [noProviders, setNoProviders] = useState(false);
  const dispatch = useDispatch();

  const disabledFields = props.disabledFields || [];

  useEffect(() => {
    UserService.getStates()
      .then((res) => {
        const stateList: states[] = (res.data || []).map((state) => ({
          key: state,
          value: state,
        }));
        stateList.unshift({ key: "Select State", value: "", disabled: true });
        setStates(stateList);
      })
      .catch((error) => toast.error("Unable to retreive state list"));
  }, []);

  const nextStep = user?.bypassPlatformFees ? "provider" : "insurance";
  const initialValues = {
    onboardingProgress: nextStep,
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    dateOfBirth: user?.dateOfBirth || "",
    phone: user?.phone || "",
    state: user?.state || "",
    address: user?.address || "",
    city: user?.city || "",
    zipcode: user?.zipcode || "",
    gender: user?.gender || "",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...props.initialValues,
  };

  // Zod validation schema
  const validationSchema = z.object({
    firstName: z
      .string()
      .regex(/^[A-Za-z\s]+$/, "First name must contain only letters")
      .min(1, "First name is required"),
    lastName: z
      .string()
      .regex(/^[A-Za-z\s]+$/, "Last name must contain only letters")
      .min(1, "Last name is required"),
    phone: z
      .string()
      .refine((val) => {
        if (!val) return false;
        const numericValue = val.replace(/\D/g, "");
        return numericValue.length === 10;
      }, "Phone number must be 10 digits")
      .transform((val) => val.replace(/\D/g, "")),
    city: z.string().min(1, "City is required"),
    zipcode: z
      .string()
      .regex(/^\d{5}(-\d{4})?$/, "Invalid Zip Code")
      .min(1, "Zip Code is required"),
    state: z.string().min(1, "State is required"),
    address: z.string().min(1, "Address is required"),
    gender: z.string().min(1, "Gender is required"),
    dateOfBirth: z.string().refine((val) => {
      if (!val) return false;
      const date = new Date(val);
      const now = new Date();
      return date < now;
    }, "Date of birth must be in the past"),
  });

  const formSchema: FormField[] = [
    {
      type: "input",
      name: "firstName",
      label: "First Name",
      fieldProps: {
        disabled: disabledFields.includes("firstName"),
        placeholder: "First Name",
      },
    },
    {
      type: "input",
      name: "lastName",
      label: "Last Name",
      fieldProps: {
        disabled: disabledFields.includes("lastName"),
        placeholder: "Last Name",
      },
    },
    {
      type: "date",
      name: "dateOfBirth",
      label: "Date of Birth",
      fieldProps: {
        disabled: disabledFields.includes("dateOfBirth"),
        format: "MM/DD/YYYY",
      },
    },
    {
      type: "pattern",
      name: "phone",
      label: "Phone Number",
      fieldProps: {
        disabled: disabledFields.includes("phone"),
        placeholder: "Phone Number",
        format: "(###) ###-####",
      },
    },
    {
      type: "select",
      name: "state",
      label: "State",
      fieldProps: {
        disabled: disabledFields.includes("state"),
        placeholder: "Select State",
      },
      options: states.map((state) => ({
        label: state.key,
        value: state.value,
        disabled: state.disabled,
      })),
    },
    {
      type: "input",
      name: "address",
      label: "Street Address",
      fieldProps: {
        disabled: disabledFields.includes("address"),
        placeholder: "Street Address",
      },
    },
    {
      type: "group-input",
      name: "city-zip",
      fieldProps: {
        direction: "row",
        spacing: 2,
      },
      group: [
        {
          type: "input",
          name: "city",
          label: "City",
          fieldProps: {
            disabled: disabledFields.includes("city"),
            placeholder: "City",
            sx: { display: "flex" },
          },
        },
        {
          name: "zipcode",
          label: "Zipcode",
          type: "input",
          rule: regex.zipcode,
          fieldProps: {
            formatterRules: ["numbers", "-"],
            formatOnChange: (value) => {
              const numericValue = value.replace(/\D/g, "");
              return numericValue.slice(0, 5) + (numericValue.length > 5 ? "-" + numericValue.slice(5, 9) : "");
            },
            sx: { display: "flex" }
          },
        },
      ],
    },
    {
      type: "toggle-buttons",
      name: "gender",
      label: "Biological Sex",
      fieldProps: {
        disabled: disabledFields.includes("gender"),
      },
      options: [
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
      ],
    },
  ];

  const onSubmit = (values: any) => {
    if (props.profile) delete values.onboardingProgress;

    UserService.updateProfile(
      {
        ...values,
        chatbotProgress: {
          sheepa: 16,
          sheepaFinished: false,
          sheepaTime: moment().utc(),
        },
      },
      user.id,
    )
      .then((res) => {
        AuthService.updateUserInfo(res.data);
        props.onContinue(user, []);
      })
      .catch((err) => toast.error(err.message));
  };

  if (noProviders) return <NoProviders />;

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Typography variant="h5" fontWeight="bold">
        {props.title || "Personal Info"}
      </Typography>
      <Typography variant="body1">
        {props.subtitle || "Please enter your demographic info"}
      </Typography>

      <Box sx={{ px: 2, py: 1, backgroundColor: "background.paper", borderRadius: 2 }}>
        <FormEngine
          schema={formSchema}
          data={initialValues}
          validation={validationSchema}
          onSubmit={onSubmit}
          submitBtnTitle={props.profile ? "Save" : "Continue"}
          submitBtnProps={{
            sx: { alignSelf: "flex-end" }
          }}
          format={{
            fullWidthButtons: false,
            buttonsDirection: "column",
            spacing: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export default PersonalInfo;